<template>
  <div>
    <v-dialog @keydown.esc="dialogOpen=false" v-model="dialogOpen" persistent max-width="640px">
      <v-card>
        <div class="d-flex justify-end">
          <v-btn
            fab
            small
            color="transparent"
            depressed
            @click="closeDialog()"
            class="mr-1 mt-1"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-title class="dialogTitle">{{ $t('expenses.bankReconciliation.relations.addRelatedTransactionsDialog.title') }}</v-card-title>
        <v-card-text class="d-flex flex-column justify-center">
          <div v-if="table.data.length">
            <v-data-table
              :loading-text="table.loadingText"
              :loading="table.loading"
              :headers="table.headers"
              :items="table.data"
              :options="table.options"
              hide-default-footer
              class="table"
            >
              <template v-slot:[`item.date`]="{ item }">
                <div>{{ $moment(item.date).format('DD/MM/YYYY') }}</div>
              </template>
              <template v-slot:[`item.description`]="{ item }">
                <div>{{ item.description }}</div>
              </template>
              <template v-slot:[`item.amount`]="{ item }">
                <div class="text-right">{{ item.amount | toFixNumber(2) | numberWithComma | prefix('$') }}</div>
              </template>
              <template v-slot:[`item.editor`]="{ item }">
                <div>{{ item.editor }}</div>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <div v-if="!item.related">
                  <v-btn fab x-small color="primary" @click="addRelatedTransaction(item._id)">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
          <div v-else class="noInfo mt-2">
            {{ $t('expenses.bankReconciliation.relations.addRelatedTransactionsDialog.table.noInfo') }}
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import formatCurrency from '@/utils/formatCurrency'

export default {
  name: 'AddRelatedTransactionsViewer',
  props: {
    bankId: String,
    accountId: String
  },
  emits: ['updateRelations', 'updateTransactions'],
  data () {
    return {
      overlay: false,
      dialogOpen: false,
      table: {
        loadingText: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedTransactionsDialog.table.loadingText'),
        loading: false,
        headers: [
          { text: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedTransactionsDialog.table.headers.date'), value: 'date', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedTransactionsDialog.table.headers.description'), value: 'description', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedTransactionsDialog.table.headers.amount'), value: 'amount', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedTransactionsDialog.table.headers.editor'), value: 'editor', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.addRelatedTransactionsDialog.table.headers.actions'), value: 'actions', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: -1,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: [],
          sortDesc: []
        }
      },
      relationId: undefined
    }
  },
  watch: {
    dialogOpen: function (val) {
      if (val) {
        this.loadTransactions()
      }
    }
  },
  methods: {
    openDialog (relationId) {
      this.relationId = relationId
      this.dialogOpen = true
    },
    closeDialog () {
      this.dialogOpen = false
    },
    loadTransactions: async function (params = null) {
      params = {
        bank: this.bankId || undefined,
        accountId: this.accountId || undefined,
        isCharge: 'true',
        ...params
      }
      this.overlay = true
      try {
        const response = await this.$api.get(`/v1/banks/transactions`, params)
        this.table.data = response.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    addRelatedTransaction: async function (bankTransactionId) {
      const payload = {
        bankTransactions: [ bankTransactionId ],
        orders: [],
        transfers: [],
        updatedBy: this.$store.state.login.user._id
      }
      this.overlay = true
      try {
        await this.$api.put(`/v1/bank-reconciliation/${this.relationId}`, payload)
        // Reload transactions
        this.loadTransactions()
        // Reload related transactions
        this.$emit('updateRelatedTransactions')
        // Reload relations
        this.$emit('updateRelations')
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    formatCurrency
  }
}
</script>

<style lang="scss" scoped>
  .dialogTitle {
    display: flex;
    justify-content: center;
    padding-top: 0 !important;
    font-size: 22px !important;
    color: #01D28E;
  }

  .table {
    overflow-y: auto;
    max-height: 65vh;

    & tr {
      border: 1px solid #ACABAB;
      border-radius: 7px;

      &:first-child {
        border-left: 1px solid #ACABAB;
        border-top-left-radius: 7px;
        border-bottom-left-radius: 7px;
      }
    }

    & td {
      border-top: 1px solid #ACABAB;
      border-bottom: 1px solid #ACABAB;

      &:last-child {
        border-right: 1px solid #ACABAB;
        border-top-right-radius: 7px;
        border-bottom-right-radius: 7px;
      }
    }

    & thead tr:last-child th {
      border-bottom: none !important;
    }
  }

  .sumText {
    margin-top: 23px;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #3F3F3F;
  }

  .noInfo {
    border: 1px solid #ACABAB;
    border-radius: 7px;
    padding-top: 22px;
    padding-bottom: 22px;
    text-align: center;
    line-height: 19px;
    color: #ACABAB;
  }
</style>
